import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X, Sparkles, Gavel } from "lucide-react";
import { useCart } from "@/store/cart";
import { useState } from "react";

const links = [
  { to: "/shop", label: "Shop" },
  { to: "/resin-lab", label: "Resin Lab", accent: true },
  { to: "/auction", label: "Auction Room", auction: true },
  { to: "/workshops", label: "Workshops" },
  { to: "/custom-order", label: "Custom Orders" },
  { to: "/care", label: "Care & Journal" },
  { to: "/about", label: "About" },
];

export default function Header() {
  const count = useCart((s) => s.count());
  const setOpen = useCart((s) => s.setOpen);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-[#004D40]/95 border-b border-[#00332B] shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 lg:h-20 lg:py-0 flex items-center justify-between gap-3">
        <Link
          to="/"
          className="flex items-center min-w-0 flex-1"
          data-testid="logo-link"
          aria-label="David's Chopping Boards — home"
        >
          <span className="font-display !font-bold text-2xl sm:text-3xl md:text-4xl !text-[#E0F2F1] leading-tight tracking-tight">
            David's Chopping Boards
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "")}`}
              className={({ isActive }) =>
                `text-sm tracking-wider uppercase transition-colors inline-flex items-center gap-1.5 ${
                  l.auction
                    ? isActive
                      ? "text-[#F5A623]"
                      : "text-[#F5A623] hover:text-[#FFD27A]"
                    : l.accent
                      ? isActive
                        ? "text-[#B87333]"
                        : "text-[#D9A46A] hover:text-[#B87333]"
                      : isActive
                        ? "text-[#4DD0C1]"
                        : "text-[#B2DFDB] hover:text-white"
                }`
              }
            >
              {l.accent && <Sparkles size={14} className="animate-pulse" aria-hidden="true" />}
              {l.auction && <Gavel size={14} aria-hidden="true" />}
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1 sm:gap-4 flex-shrink-0">
          <button
            data-testid="cart-btn"
            onClick={() => setOpen(true)}
            className="relative text-[#E0F2F1] hover:text-[#4DD0C1] transition-colors p-2"
            aria-label="Open cart"
          >
            <ShoppingBag size={20} />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-1 -right-1 bg-[#00BFA5] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden text-[#E0F2F1] p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-[#00332B] bg-[#004D40] px-6 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-${l.to.replace("/", "")}`}
              onClick={() => setMobileOpen(false)}
              className={`text-sm tracking-wider uppercase inline-flex items-center gap-2 ${
                l.auction ? "text-[#F5A623]" : l.accent ? "text-[#D9A46A]" : "text-[#E0F2F1]"
              }`}
            >
              {l.accent && <Sparkles size={14} className="animate-pulse" aria-hidden="true" />}
              {l.auction && <Gavel size={14} aria-hidden="true" />}
              {l.label}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
}
